import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentMedical, faPlus } from "@fortawesome/free-solid-svg-icons";
import "react-chat-elements/dist/main.css";
import Notification from "../../../Components/Core/Notification";
import Loader from "../../../Components/Core/Loader";
import ConfirmModal from "../../../Components/Modals/ConfirmModal";
import { RequestConsultationPayment } from "../../../Services/ConsultionService";
import SupportWidget from "../../../Components/Core/SupportWidget";
import { GetUserConsultations } from "../../../Services/ConsultionService";
import ChatActive from "./Chats-Index-Active";
import ChatHistory from "./Chat-Index-History";
import "./Chat-Index.css";
import { useTenantContent } from "../../../Context/TenantContentContext";
import { getDecodedUserId } from "../../../Services/GeneralService";
import { CheckRole } from "../../../Services/AuthService";
import { redirectToPaymentGateway } from "../../../Services/paymentRedirect";
import GatewaySelector from "../../../Components/Payment/GatewaySelector";

const ActiveChatPage = () => {
  const [activeSession, setActiveSession] = useState(null);
  const [historySessions, setHistorySessions] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState("zarinpal");
  const [consultationFee, setConsultationFee] = useState(null);
  const [
    isRevisedConsultationFeatureState,
    setIsRevisedConsultationFeatureState,
  ] = useState(null);

  const [revisedConsultationDays, setRevisedConsultationDays] = useState(null);

  const navigate = useNavigate();
  const { content } = useTenantContent();

  const patientId = getDecodedUserId();

  const paymentGatewayMethod = Number(content?.paymentGatewayMethod ?? 1);
  const isAdmin = CheckRole(["Admin"]);
  const isPublicGateway = paymentGatewayMethod === 1;
  const canShowGatewaySelector = isPublicGateway && isAdmin;

  const handleRequestConsultation = async () => {
    try {
      setLoading(true);

      const gateway = canShowGatewaySelector ? selectedGateway : "zarinpal";
      localStorage.setItem("consultationGateway", gateway);

      const requestResponse = await RequestConsultationPayment(gateway);

      if (
        requestResponse.code === 100 &&
        (requestResponse.authority || requestResponse.token)
      ) {
        redirectToPaymentGateway(requestResponse);
      } else {
        console.log("Payment request failed!");
      }
    } catch (error) {
      setLoading(false);
      setSnackbarMessage(error.message);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      console.error(error);
    } finally {
      setOpenDialog(false);
    }
  };

  useEffect(() => {
    const loadConsultations = async () => {
      let redirected = false;
      try {
        document.title = "لیست مشاوره ها";

        setLoading(true);
        const consultations = await GetUserConsultations();

        if (consultations.status === 204) {
          redirected = true;
          navigate("/counseling/index", { replace: true });
          return;
        }

        const data = await consultations.json();
        const hasActive = Boolean(data.activeConsultation?.id);
        const hasHistory = (data.consultationHistories || []).length > 0;

        if (!hasActive && !hasHistory) {
          redirected = true;
          navigate("/counseling/index", { replace: true });
          return;
        }

        setActiveSession(data.activeConsultation);
        setHistorySessions(data.consultationHistories);
        setConsultationFee(data.consultationFee);
        setIsRevisedConsultationFeatureState(
          data.isRevisedConsultationFeatureState,
        );
        setRevisedConsultationDays(data.revisedConsultationDays);
      } catch (error) {
        setSnackbarMessage(error.message);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } finally {
        if (!redirected) {
          setLoading(false);
        }
      }
    };
    loadConsultations();
  }, [patientId, navigate]);

  const handleJoinChat = (sessionId) => {
    navigate(`/counseling/chat/${sessionId}`, {
      state: { from: "/counseling/chat/index" },
    });
  };

  const feeInThousands =
    typeof consultationFee === "number"
      ? (consultationFee / 1000).toLocaleString()
      : "--";

  if (!content) {
    return <Loader />;
  }

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="consultation-page">
          <div className="page-container iran-sans">
            {/* Page Header */}
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <FontAwesomeIcon icon={faCommentMedical} />
                </div>
                <div className="header-text">
                  <h1>مشاوره های من</h1>
                  <p>مدیریت و پیگیری جلسات مشاوره آنلاین</p>
                </div>
              </div>
              <button
                className="request-btn iran-sans"
                onClick={() => setOpenDialog(true)}
                disabled={activeSession !== null}
              >
                <FontAwesomeIcon icon={faPlus} />
                درخواست مشاوره جدید
              </button>
            </div>

            {activeSession ? (
              <>
                <ChatActive
                  activeSession={activeSession}
                  handleJoinChat={handleJoinChat}
                />
                <ChatHistory
                  historySessions={historySessions}
                  handleJoinChat={handleJoinChat}
                />
              </>
            ) : (
              <>
                <ChatHistory
                  historySessions={historySessions}
                  handleJoinChat={handleJoinChat}
                />
                <ChatActive
                  activeSession={activeSession}
                  handleJoinChat={handleJoinChat}
                />
              </>
            )}
          </div>

          <Notification
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            snackbarSeverity={snackbarSeverity}
            setOpenSnackbar={setOpenSnackbar}
          />

          <ConfirmModal
            openDialog={openDialog}
            handleCloseDialog={() => setOpenDialog(false)}
            handleConfirm={handleRequestConsultation}
            text={[
              <>
                آیا مطمئن هستید که می‌خواهید مشاوره آنلاین با دکتر را شروع کنید؟
                هزینه مشاوره مبلغ <strong>{feeInThousands} هزار تومان</strong>{" "}
                است. در صورت تایید، به صفحه پرداخت هدایت خواهید شد.
                {isRevisedConsultationFeatureState && (
                  <p style={{ color: "#388e3c", fontWeight: "bold" }}>
                    این مبلغ با تخفیف ویژه برای مراجعینی که در{" "}
                    {revisedConsultationDays} روز گذشته مشاوره‌ای داشته‌اند
                    محاسبه شده است.
                  </p>
                )}
              </>,
              ...(content?.consultationPaymentModalContent ?? []),
            ]}
            cancleText="خیر"
            confirmText="تایید و پرداخت"
            title="درخواست مشاوره"
          >
            {canShowGatewaySelector && (
              <div style={{ marginTop: "12px" }}>
                <GatewaySelector
                  selectedGateway={selectedGateway}
                  onChange={setSelectedGateway}
                />
              </div>
            )}
          </ConfirmModal>

          <SupportWidget />
        </div>
      )}
    </>
  );
};

export default ActiveChatPage;
